import { FormControl, Validators } from '@angular/forms';
import Papa from 'papaparse';

/**
 * Why a row of a Participant import file will or won't be invited.
 * Every status except `will-invite` is a skipped row.
 */
export type ParticipantImportStatus =
  | 'will-invite'
  | 'missing-name'
  | 'invalid-email'
  | 'on-roster'
  | 'has-trip-access'
  | 'duplicate';

export interface ParticipantImportRow {
  firstname: string;
  lastname: string;
  email: string;
  status: ParticipantImportStatus;
}

export interface ParticipantImportTrip {
  rosterEmails: readonly string[];
  nonRosterEmails: readonly string[];
}

export interface ParticipantImportResult {
  /** One entry per data row, in file order. Empty when `tooManyRows`. */
  rows: ParticipantImportRow[];
  /** The file has more than `MAX_PARTICIPANT_IMPORT_ROWS` data rows and is refused. */
  tooManyRows: boolean;
}

/**
 * Most data rows one import may hold. The server sends invitations one by one,
 * so a bigger file risks the request timing out.
 */
export const MAX_PARTICIPANT_IMPORT_ROWS = 100;

const PLACEHOLDER_EMAIL_DOMAIN = '@myppm.org';

/**
 * Parses a Participant import CSV (first name, last name, email by position,
 * header row optional) and gives every data row a status. Pure: no DI, no I/O.
 * Valid rows are then matched (trimmed, case-insensitive) against the trip and
 * earlier rows; the first invitable occurrence of an email wins.
 */
export function sortParticipantImport(
  text: string,
  trip: ParticipantImportTrip,
): ParticipantImportResult {
  const parsed = Papa.parse<string[]>(text, { skipEmptyLines: 'greedy' });
  const records = parsed.data.map((record) => ({
    firstname: (record[0] ?? '').trim(),
    lastname: (record[1] ?? '').trim(),
    email: (record[2] ?? '').trim(),
  }));

  if (records.length > 0 && !isEmailFormat(records[0].email)) {
    records.shift();
  }

  if (records.length > MAX_PARTICIPANT_IMPORT_ROWS) {
    return { rows: [], tooManyRows: true };
  }

  const rosterEmails = emailKeys(trip.rosterEmails);
  const nonRosterEmails = emailKeys(trip.nonRosterEmails);
  // Only will-invite rows claim an email, so the first invitable row wins.
  const seen = new Set<string>();

  return {
    rows: records.map((record) => {
      let status = validityStatus(record);
      if (status === 'will-invite') {
        const key = emailKey(record.email);
        if (rosterEmails.has(key)) {
          status = 'on-roster';
        } else if (nonRosterEmails.has(key)) {
          status = 'has-trip-access';
        } else if (seen.has(key)) {
          status = 'duplicate';
        } else {
          seen.add(key);
        }
      }
      return { ...record, status };
    }),
    tooManyRows: false,
  };
}

/**
 * The roster size change the server makes when the checked rows outnumber the
 * spaces left: it grows the size to the roster count plus the new participants.
 * Null when they fit.
 */
export function rosterSizeChange(roster: {
  attendees: number;
  rosterCount: number;
  checkedCount: number;
}): { from: number; to: number } | null {
  const to = roster.rosterCount + roster.checkedCount;
  return to > roster.attendees ? { from: roster.attendees, to } : null;
}

/** How many of `sentEmails` appear in `rosterEmails`, matched trimmed and case-insensitive. */
export function countLandedEmails(sentEmails: readonly string[], rosterEmails: readonly string[]): number {
  const roster = emailKeys(rosterEmails);
  return sentEmails.filter((email) => roster.has(emailKey(email))).length;
}

function emailKey(email: string): string {
  return email.trim().toLowerCase();
}

function emailKeys(emails: readonly string[]): Set<string> {
  return new Set(emails.map(emailKey).filter((key) => key !== ''));
}

function validityStatus(record: Omit<ParticipantImportRow, 'status'>): ParticipantImportStatus {
  if (!record.firstname || !record.lastname) {
    return 'missing-name';
  }
  if (!isEmailFormat(record.email) || record.email.toLowerCase().endsWith(PLACEHOLDER_EMAIL_DOMAIN)) {
    return 'invalid-email';
  }
  return 'will-invite';
}

/** The same check Quick Add's form applies (`Validators.required` + `Validators.email`). */
function isEmailFormat(email: string): boolean {
  return email !== '' && Validators.email(new FormControl(email)) === null;
}
